/**
 * EmploymentTimeline — Chronologische Darstellung der Vertragshistorie eines Mitarbeiters.
 * Zeigt pro Record eine Card mit Diff-Highlight (Änderungen gegenüber Vorgänger).
 */

import { Edit3, Trash2, Plus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { EmploymentHistoryRecord } from '@/types';
import {
  HISTORY_FIELD_LABELS,
  formatHistoryValue,
  getChangedFields,
} from '@/utils/historyLabels';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EmploymentTimelineProps {
  records: EmploymentHistoryRecord[];
  onAddNew: () => void;
  onEdit: (record: EmploymentHistoryRecord) => void;
  onDelete: (recordId: string) => void;
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Formatiert ein ISO-Datum (YYYY-MM-DD) als DD.MM.YYYY */
function formatDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}

/** Formatiert einen ISO-DateTime-String als DD.MM.YYYY */
function formatDateTime(iso: string): string {
  return formatDate(iso.slice(0, 10));
}

type RecordStatus = 'current' | 'past' | 'future';

/** Bestimmt den Status eines Records relativ zu heute. */
function getRecordStatus(record: EmploymentHistoryRecord): RecordStatus {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  if (record.validFrom > todayStr) return 'future';
  if (record.validTo === null || record.validTo >= todayStr) return 'current';
  return 'past';
}

const STATUS_CONFIG: Record<
  RecordStatus,
  { label: string; badgeVariant: 'success' | 'default' | 'info'; dotColor: string }
> = {
  current: {
    label: 'Aktuell',
    badgeVariant: 'success',
    dotColor: 'bg-green-500 dark:bg-green-400',
  },
  past: {
    label: 'Vergangen',
    badgeVariant: 'default',
    dotColor: 'bg-muted-foreground',
  },
  future: {
    label: 'Zukünftig',
    badgeVariant: 'info',
    dotColor: 'bg-blue-500 dark:bg-blue-400',
  },
};

// ---------------------------------------------------------------------------
// Sub-Components
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-32 w-full" />
      ))}
    </div>
  );
}

function EmptyState({ onAddNew }: { onAddNew: () => void }) {
  return (
    <div className="rounded-lg border border-dashed p-8 text-center">
      <p className="text-sm text-muted-foreground">
        Noch keine Vertragshistorie vorhanden. Klicke{' '}
        <button
          type="button"
          onClick={onAddNew}
          className="font-medium text-foreground underline underline-offset-4 hover:text-foreground/80"
        >
          Neue Änderung erfassen
        </button>{' '}
        um den ersten Eintrag zu erstellen.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline Record Card
// ---------------------------------------------------------------------------

interface TimelineRecordProps {
  record: EmploymentHistoryRecord;
  predecessor: EmploymentHistoryRecord | null;
  onEdit: (record: EmploymentHistoryRecord) => void;
  onDelete: (recordId: string) => void;
}

function TimelineRecord({ record, predecessor, onEdit, onDelete }: TimelineRecordProps) {
  const status = getRecordStatus(record);
  const config = STATUS_CONFIG[status];
  const changedFields = getChangedFields(record, predecessor);

  return (
    <div className="relative pl-8 pb-6 last:pb-0">
      {/* Dot on the timeline */}
      <div
        className={cn(
          'absolute left-0 top-3 h-2 w-2 -translate-x-[5px] rounded-full ring-2 ring-background',
          config.dotColor
        )}
      />

      <Card className="shadow-none">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 p-4 pb-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">
              Ab {formatDate(record.validFrom)}
              {record.validTo && (
                <span className="font-normal text-muted-foreground">
                  {' '}– {formatDate(record.validTo)}
                </span>
              )}
            </span>
            <Badge variant={config.badgeVariant}>{config.label}</Badge>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onEdit(record)}
                  >
                    <Edit3 className="h-4 w-4" />
                    <span className="sr-only">Bearbeiten</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Bearbeiten</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => onDelete(record.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Löschen</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Löschen</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        <CardContent className="pt-3">
          {/* Changed fields diff */}
          {changedFields.length > 0 ? (
            <div className="space-y-1.5">
              {changedFields.map((field) => {
                const label = HISTORY_FIELD_LABELS[field];
                const currentValue = record[field as keyof EmploymentHistoryRecord];
                const previousValue = predecessor
                  ? predecessor[field as keyof EmploymentHistoryRecord]
                  : null;

                return (
                  <div key={field} className="text-sm">
                    <span className="text-muted-foreground">{label}:</span>{' '}
                    <span className="font-medium">
                      {formatHistoryValue(field, currentValue)}
                    </span>
                    {predecessor && (
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        &larr; war: {formatHistoryValue(field, previousValue)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Keine Änderungen gegenüber dem Vorgänger.</p>
          )}

          {/* Audit line */}
          {(record.changedByName || record.changeNote) && (
            <>
              <Separator className="my-3" />
              <div className="space-y-0.5">
                {record.changedByName && (
                  <p className="text-xs text-muted-foreground">
                    Geändert von {record.changedByName} am {formatDateTime(record.createdAt)}
                  </p>
                )}
                {record.changeNote && (
                  <p className="text-xs italic text-muted-foreground">{record.changeNote}</p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function EmploymentTimeline({
  records,
  onAddNew,
  onEdit,
  onDelete,
  isLoading,
}: EmploymentTimelineProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-10 w-48" />
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold tracking-tight">Vertragshistorie</h3>
        <Button onClick={onAddNew}>
          <Plus className="mr-2 h-4 w-4" />
          Neue Änderung erfassen
        </Button>
      </div>

      {/* Timeline or empty state */}
      {records.length === 0 ? (
        <EmptyState onAddNew={onAddNew} />
      ) : (
        <div className="relative ml-1 border-l-2 border-muted">
          {records.map((record, index) => {
            // Predecessor is the NEXT item (records sorted DESC by validFrom)
            const predecessor = index < records.length - 1 ? records[index + 1] ?? null : null;

            return (
              <TimelineRecord
                key={record.id}
                record={record}
                predecessor={predecessor}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
